import { tryLoadProperties } from "@/util/wordUtil";
import imageData from "@/services/word/imageDataService";
import imageUrlSubtitutor from "@/services/html/imageUrlSubstitutor";
import logger from "@/util/logger";
import {
    REPEATING_CONTENT_BLOCK,
    REPEATING_TABLE_BLOCK,
} from "@/constants/contentControlTypes";

const getParentContentControl = (context, range) => new Promise(resolve => {
    const parent = range.parentContentControlOrNullObject;
    tryLoadProperties(context, parent, "isNull").then(success => {
        if (success && !parent.isNull) {
            resolve(parent);
        } else {
            resolve(null);
        }
    });
});

const insert = async (title, options = {}, callback = null) => {
    logger.debug("Inserting new content control", { title, options });

    await Word.run(async context => {
        const range = context.document.getSelection();
        const parentContentControl = await getParentContentControl(context, range);
        const contentControl = range.insertContentControl();
        contentControl.title = title;

        if (options.placeholderText) {
            contentControl.placeholderText = options.placeholderText
        }

        if (options.color) {
            contentControl.color = options.color;
        }

        if (options.tag) {
            contentControl.tag = options.tag;
        }

        if (options.hideBoundingBox) {
            contentControl.appearance = "Hidden";
        }

        await context.sync();

        logger.debug("Content control inserted.", { title, options });

        if (callback) {
            await callback({
                contentControl,
                parentContentControl,
                context
            });
        }
    });
}

const _get = async (id, collection, context) => {
    const control = collection.getByIdOrNullObject(id);
    await context.sync();
    if (!control.isNullObject) {
        return control;
    } else {
        await tryLoadProperties(context, collection, "items");
        for (let i = 0; i < collection.items.length; i++) {
            const childControl = collection.items[i];
            await tryLoadProperties(context, childControl, "contentControls")
            const matchedControl = await _get(id, childControl.contentControls, context)
            if (matchedControl) {
                return matchedControl;
            }
        }

        return null;
    }
}

const _getById = async (id, context) => {
    await tryLoadProperties(context, context.document, "contentControls");
    const matchedControl = await _get(id, context.document.contentControls, context);
    if (matchedControl) {
        return matchedControl;
    } else {
        return null;
    }
}

const get = id => new Promise(resolve => {
    Word.run(async context => {
        const matchedControl = await _getById(id, context);
        if (matchedControl) {
            resolve(matchedControl);
        } else {
            resolve(null);
        }
    });
});

const _delete = async (id, keepContent = false) => {
    logger.debug("Deleting content control.", { id, keepContent });

    await Word.run(async context => {
        const matchedControl = await _getById(id, context);
        if (matchedControl) {
            matchedControl.delete(keepContent);
            await context.sync();
            logger.debug("Content control deleted.", { id });
        }
    });
}

const select = async id => {
    await Word.run(async context => {
        const matchedControl = await _getById(id, context);
        if (matchedControl) {
            const range = matchedControl.getRange("Content");
            range.select();

            await context.sync();
        }
    });
}

const deselect = async () => {
    await Word.run(async context => {
        const range = context.document.getSelection();
        const currentContentControl = await getParentContentControl(context, range);
        if (currentContentControl) {
            const newRange = currentContentControl.getRange("After");
            newRange.select();

            await context.sync();
        }
    });
}

const _asPlaceholder = async (contentControl, context) => {
    if (await tryLoadProperties(context, contentControl, "title")) {
        const title = contentControl.title;
        if (title.startsWith("Sharedo")) {
            const match = /^Sharedo (?<type>[A-Za-z]+): (?<path>\S.+)$/.exec(title);
            if (match) {
                return {
                    type: match.groups.type,
                    path: match.groups.path
                };
            }
        }
    }

    return null
}

const _populateParentPlaceholders = async (contentControl, placeholder, context) => {
    const parent = contentControl.parentContentControlOrNullObject;
    if (await tryLoadProperties(context, parent, "isNull") && !parent.isNull) {
        const parentPlaceholder = await _asPlaceholder(parent, context);
        if (parentPlaceholder) {
            placeholder.parent = parentPlaceholder

            await _populateParentPlaceholders(parent, placeholder, context);
        }
    }
}

const getCurrentPlaceholder = async () => {
    let placeholder = null;
    await Word.run(async context => {
        const range = context.document.getSelection();
        const currentContentControl = await getParentContentControl(context, range);

        if (currentContentControl) {
            placeholder = await _asPlaceholder(currentContentControl, context);

            if (placeholder) {
                await _populateParentPlaceholders(currentContentControl, placeholder, context);
            }
        }
    });

    return placeholder;
}

const isContentSelected = async () => {
    let isEmpty = false;
    await Word.run(async context => {
        const range = context.document.getSelection();
        await tryLoadProperties(context, range, "isEmpty");

        isEmpty = range.isEmpty;
    });

    return !isEmpty;
}

const getHtmlContent = async id => {
    let html = "";
    await Word.run(async context => {
        const contentControl = await _getById(id, context);
        if (contentControl) {
            const range = contentControl.getRange("Content");
            const images = await imageData.extractFrom(range);
            const htmlContent = range.getHtml();
            await context.sync();

            if (htmlContent.value) {
                html = imageUrlSubtitutor.substitute(htmlContent.value, images);
            }
        }
    });

    return html;
}

const replaceHtmlContent = async (id, html) => {
    await Word.run(async context => {
        const contentControl = await _getById(id, context);
        if (contentControl) {
            contentControl.clear();
            contentControl.insertHtml(html, "Replace");

            await context.sync();
        }
    });
}

const getClosestListPlaceholder = placeholder => {
    if (placeholder.type === REPEATING_CONTENT_BLOCK || placeholder.type === REPEATING_TABLE_BLOCK) {
        return placeholder;
    }

    let parent = placeholder.parent;
    while (parent) {
        if (parent.type === REPEATING_CONTENT_BLOCK || parent.type === REPEATING_TABLE_BLOCK) {
            return parent;
        }

        parent = parent.parent;
    }

    return null;
}

export default {
    get,
    isContentSelected,
    select,
    deselect,
    insert,
    getCurrentPlaceholder,
    replaceHtmlContent,
    getHtmlContent,
    getClosestListPlaceholder,
    "delete": _delete
}